/**
 * Capacitor Native Hooks
 * 
 * Provides Android-specific behavior:
 * - Hardware back button handling (double-tap to exit from Home)
 * - Offline/online detection with banner
 * - Splash screen auto-dismiss
 */
import { useEffect, useState, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Lazy-load Capacitor plugins to avoid crashes on web
let AppPlugin = null;
let NetworkPlugin = null;
let SplashPlugin = null;
let GeolocationPlugin = null;

async function loadPlugins() {
  try {
    const appMod = await import("@capacitor/app");
    AppPlugin = appMod.App;
  } catch {}
  try {
    const netMod = await import("@capacitor/network");
    NetworkPlugin = netMod.Network;
  } catch {}
  try {
    const splashMod = await import("@capacitor/splash-screen");
    SplashPlugin = splashMod.SplashScreen;
  } catch {}
  try {
    const geoMod = await import("@capacitor/geolocation");
    GeolocationPlugin = geoMod.Geolocation;
  } catch {}
}

/**
 * Hook: Android hardware back button
 * - On Home ("/"), shows "Press back again to exit" toast
 * - On other screens, navigates to previous screen
 */
export function useAndroidBackButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const lastBackPress = useRef(0);
  const [showExitToast, setShowExitToast] = useState(false);

  useEffect(() => {
    let listener = null;

    async function setup() {
      await loadPlugins();
      if (!AppPlugin) return;

      listener = await AppPlugin.addListener("backButton", ({ canGoBack }) => {
        const isHome = location.pathname === "/";

        if (isHome) {
          const now = Date.now();
          if (now - lastBackPress.current < 2000) {
            // Second press within 2 seconds — exit app
            AppPlugin.exitApp();
          } else {
            // First press — show toast
            lastBackPress.current = now;
            setShowExitToast(true);
            setTimeout(() => setShowExitToast(false), 2000);
          }
        } else {
          // Not on home — go back
          navigate(-1);
        }
      });
    }

    setup();
    return () => { if (listener) listener.remove(); };
  }, [location.pathname, navigate]);

  return { showExitToast };
}

/**
 * Hook: Network status (online/offline)
 */
export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(true);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    let listener = null;

    async function setup() {
      await loadPlugins();
      if (NetworkPlugin) {
        // Get initial status
        try {
          const status = await NetworkPlugin.getStatus();
          setIsOnline(status.connected);
        } catch {}

        // Listen for changes
        listener = await NetworkPlugin.addListener("networkStatusChange", (status) => {
          setIsOnline(status.connected);
        });
      } else {
        // Fallback for web browser
        setIsOnline(navigator.onLine);
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);
        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);
        return () => {
          window.removeEventListener("online", handleOnline);
          window.removeEventListener("offline", handleOffline);
        };
      }
    }

    setup();
    return () => { if (listener) listener.remove(); };
  }, []);

  const retry = useCallback(() => {
    setRetrying(true);
    setTimeout(() => {
      setIsOnline(navigator.onLine);
      setRetrying(false);
    }, 1500);
  }, []);

  return { isOnline, retrying, retry };
}

/**
 * Hook: Auto-hide splash screen after app mounts
 */
export function useHideSplash() {
  useEffect(() => {
    async function hide() {
      await loadPlugins();
      if (SplashPlugin) {
        try { await SplashPlugin.hide(); } catch {}
      }
    }
    // Small delay to let the app render first
    const timer = setTimeout(hide, 500);
    return () => clearTimeout(timer);
  }, []);
}

/**
 * Component: Offline banner
 */
export function OfflineBanner({ isOnline, retrying, retry }) {
  if (isOnline) return null;

  return (
    <div className="offline-banner slide-down">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="1" y1="1" x2="23" y2="23"/>
        <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"/>
        <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"/>
        <path d="M10.71 5.05A16 16 0 0 1 22.56 9"/>
        <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"/>
        <path d="M8.53 16.11a6 6 0 0 1 6.95 0"/>
        <line x1="12" y1="20" x2="12.01" y2="20"/>
      </svg>
      <span>No Internet Connection</span>
      <button 
        onClick={retry} 
        disabled={retrying}
        style={{ 
          marginLeft: 8, 
          padding: "4px 12px", 
          borderRadius: 6, 
          background: "rgba(255,255,255,0.2)", 
          fontSize: 12,
          fontWeight: 600,
          border: "none",
          color: "white",
          cursor: retrying ? "wait" : "pointer"
        }}>
        {retrying ? "Retrying…" : "Retry"}
      </button>
    </div>
  );
}

/**
 * Component: Exit toast
 */
export function ExitToast({ show }) {
  if (!show) return null;

  return (
    <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 fade-up"
      style={{
        background: "rgba(0,0,0,0.8)",
        color: "white",
        padding: "10px 20px",
        borderRadius: 24,
        fontSize: 13,
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}>
      Press back again to exit
    </div>
  );
}

/**
 * Hook: Request Location Permission gracefully
 */
export function useLocationPermission() {
  const [permissionState, setPermissionState] = useState("prompt");

  useEffect(() => {
    async function check() {
      await loadPlugins();
      if (GeolocationPlugin) {
        try {
          const status = await GeolocationPlugin.checkPermissions();
          setPermissionState(status.location);
        } catch {}
      } else {
        setPermissionState("granted"); // fallback on web
      }
    }
    check();
  }, []);

  const requestPermission = async () => {
    await loadPlugins();
    if (GeolocationPlugin) {
      try {
        const status = await GeolocationPlugin.requestPermissions();
        setPermissionState(status.location);
        return status.location === "granted";
      } catch {
        return false;
      }
    }
    return true; // web
  };

  return { permissionState, requestPermission };
}
